import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Person } from '@domain/entities/Person';
import { ROLES, ROLE_LABEL, type Role } from '@domain/vocabulary';
import { whyRoleUnavailable } from '@domain/policies/RolePolicy';
import { Banner, Button, Modal } from '@presentation/design-system';
import { useRuntimeConfig } from '@presentation/providers/RuntimeConfigProvider';
import { useSessionState } from '@presentation/providers/SessionProvider';
import { reasonFor } from '@presentation/format';
import { getServices } from '@infrastructure/services';
import styles from './RoleChangeAction.module.css';

/**
 * Cambiar el rol de alguien ya registrado.
 *
 * Antes sólo se podía **al darlo de alta**. Después, la única palanca era el par
 * «hacer líder / quitar de líderes» de Sub áreas y líderes, que sabe ir de
 * `empleado` a `lider` y volver, y nada más: nombrar RR.HH. o Finanzas a alguien
 * que ya estaba en la casa no tenía forma. Se notó cuando en producción llevaba
 * meses sin una sola persona en Finanzas, con el rol perfectamente definido.
 *
 * ## Por qué hay una comprobación antes del selector
 *
 * Aprobar no lo da el puesto sino el permiso `aprobar`, que en la matriz sembrada
 * sólo tienen `lider` y `super`. A quién le firma cada quien lo dicen las filas de
 * `personLeaders`, que **no** miran el rol. Nada ataba las dos cosas, y el
 * resultado ya está en producción: una persona firmada por un `admin` cuyas
 * aprobaciones rechaza el servidor por falta de permiso, en silencio.
 *
 * Así que los roles que no pueden aprobar salen deshabilitados mientras la persona
 * tenga gente a cargo, diciendo cuánta. El servidor vuelve a comprobarlo —esto es
 * cortesía, no seguridad—, pero enterarse al pulsar es peor que no poder pulsar.
 *
 * ## Lo que no hace, y por qué
 *
 * No reasigna a nadie. Repartir veintiuna personas entre otros líderes es una
 * decisión con nombres propios y se toma en la pantalla que los tiene delante;
 * hacerlo aquí en automático dejaría el trabajo hecho a medias y sin dueño.
 */

interface RoleChangeActionProps {
  readonly person: Person;
  /** El mismo par que exige el servidor: `admin` para su área, `areas` para todas. */
  readonly canChange: boolean;
  readonly onDone: () => Promise<void>;
}

/** Una línea por rol: qué gana quien lo recibe. Lo que el nombre no dice. */
const ROLE_NOTE: Readonly<Record<Role, string>> = {
  empleado: 'Pide sus días y ve el calendario de su área.',
  lider: 'Aprueba las solicitudes de quien tenga a cargo y ve los saldos de su equipo.',
  admin: 'Administra el área: personas, cuentas y catálogos. No aprueba solicitudes.',
  finanzas: 'Ve y edita la compensación. No entra al resto del portal.',
  global:
    'Ve y administra las ocho áreas: personas, cuentas y expedientes de toda la empresa. No aprueba solicitudes ni toca la configuración.',
  super: 'Todo el portal y todas las áreas, incluida la configuración global.',
};

export const RoleChangeAction = ({
  person,
  canChange,
  onDone,
}: RoleChangeActionProps): ReactNode => {
  const config = useRuntimeConfig();
  const { status } = useSessionState();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<Role>(person.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * A cuánta gente le firma. Se cuenta por área y no con `reportsOf`.
   *
   * `reportsOf` filtra sólo por `leaderPersonId`, y la regla de `personLeaders`
   * autoriza el listado por `inScope(resource.data.areaId)` —o por ser uno de los
   * dos extremos del vínculo—. **Una regla que nombra `resource` sólo la cumple
   * una consulta que filtre por ese campo**, así que `reportsOf` sobre un tercero
   * falla para todo el que no sea superusuario, que es justo quien más va a usar
   * esta pantalla. Filtrando por área se prueba `inScope` y funciona para los dos.
   */
  const reports = useQuery({
    queryKey: ['personDetail', 'reports', person.id, person.areaId],
    queryFn: async () => {
      const result = await getServices().leaders.listByArea(person.areaId);
      if (!result.ok) throw new Error(reasonFor(result.error));
      return result.value.filter((link) => link.leaderPersonId === person.id).length;
    },
    enabled: open,
  });

  const reportCount = reports.data ?? 0;
  const callerRole = status.kind === 'ready' ? status.session.role : null;

  const close = (): void => {
    setOpen(false);
    setChoice(person.role);
    setError(null);
  };

  /*
   * Por qué un rol no se puede elegir ahora mismo, o `null` si sí se puede. La
   * regla vive en el dominio; aquí sólo se le pasa lo que la pantalla sabe.
   *
   * Mientras la cuenta está en vuelo no se bloquea nada: deshabilitar por un cero
   * que todavía no es una respuesta enseñaría lo contrario de lo que se sabe.
   */
  const blockedReason = (role: Role): string | null =>
    whyRoleUnavailable({
      role,
      currentRole: person.role,
      callerRole,
      reportCount: reports.isPending ? 0 : reportCount,
      matrix: config.rolePermissions,
    });

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const result = await getServices().people.setRole(person.id, choice);
    setBusy(false);

    if (!result.ok) {
      setError(reasonFor(result.error));
      return;
    }
    close();
    await onDone();
  };

  if (!canChange) return null;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setChoice(person.role);
          setOpen(true);
        }}
      >
        Cambiar rol
      </Button>

      {open && (
        <Modal
          title={`Rol de ${person.displayName}`}
          onClose={close}
          footer={
            <>
              <Button variant="ghost" onClick={close}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={busy || choice === person.role || blockedReason(choice) !== null}
                onClick={() => {
                  void save();
                }}
              >
                {busy ? 'Guardando…' : 'Cambiar rol'}
              </Button>
            </>
          }
        >
          <div className={styles.body}>
            {error !== null && <Banner tone="danger">{error}</Banner>}

            {reportCount > 0 && (
              <Banner tone="info">
                Ahora mismo le firma a {reportCount}{' '}
                {reportCount === 1 ? 'persona' : 'personas'}. Los roles que no aprueban
                solicitudes no se pueden elegir hasta reasignarlas desde «Sub áreas y
                líderes».
              </Banner>
            )}

            <ul className={styles.options}>
              {ROLES.map((role) => {
                const blocked = blockedReason(role);
                const current = role === person.role;
                return (
                  <li key={role}>
                    <button
                      type="button"
                      className={styles.option}
                      aria-pressed={choice === role}
                      disabled={blocked !== null}
                      onClick={() => {
                        setChoice(role);
                      }}
                    >
                      <span className={styles.optionMark} aria-hidden="true" />
                      <span className={styles.optionText}>
                        <span className={styles.optionName}>
                          {ROLE_LABEL[role]}
                          {current && <span className={styles.optionNow}>Rol actual</span>}
                        </span>
                        <span className={styles.optionNote}>{blocked ?? ROLE_NOTE[role]}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      )}
    </>
  );
};
